import React, { PropsWithChildren, ReactElement } from 'react';
import { useProjectId } from 'store/project';
import { useAuth0 } from 'services/auth0/react-auth0.spa';
import { WithEntityStoresInitializer } from 'components/common/WithEntityStoresInitializer';
import { useDataFlushedSubscription } from './useDataFlushedSubscription';
import { useProjectDataLoadingInitializer } from './useProjectDataLoadingInitializer';

function ProjectDataLoadingInitializer(): null {
  useProjectDataLoadingInitializer();
  return null;
}

function AuthenticatedSubscriptions(): null {
  useDataFlushedSubscription();
  return null;
}

/**
 * Wrapper that renders data providers only when user is authenticated.
 * This prevents unnecessary service worker calls and data fetching before login.
 *
 * All channel listeners, store initializers, and data providers that require
 * authentication should be placed here.
 */
export function WithAuthenticatedProviders({
  children,
}: PropsWithChildren): ReactElement {
  const { isAuthenticated, loading } = useAuth0();
  const projectId = useProjectId();
  // Auth0 state starts as undefined during bootstrap; providers should run only for explicit true.
  const isAuthenticatedTrue = isAuthenticated === true;

  if (loading || !isAuthenticatedTrue) {
    return <>{children}</>;
  }

  // The loading initializer sits outside the per-project initializer: it follows the project switch itself.
  return (
    <>
      <ProjectDataLoadingInitializer />
      <WithEntityStoresInitializer key={projectId}>
        <AuthenticatedSubscriptions />
        {children}
      </WithEntityStoresInitializer>
    </>
  );
}
